import { useState } from 'react'
import { Flex, useToast } from '@chakra-ui/react'
import { useMachine } from '@xstate/react'
import { RelationshipRequestModal, RequestModalBodyForm } from '~/components'
import { useAuth } from '~/auth'
import { IntroductionsApi, RelationshipLeadsApi } from '~/api'
import { RelationshipLeadStore } from '~/stores'
import { LinksContent } from './LinksContent'
import { RelationshipContent } from './RelationshipContent'
import { RequestIntroductionContent } from './RequestIntroductionContent'
import { SaveRelationshipContent } from './SaveRelationshipContent'
import { HideRelationshipContent } from './HideRelationshipContent'
import { RelationshipCardButtons } from './RelationshipCardButtons'
import { IntroductionRequestSubmission, buildRelationshipCardStateMachine } from './relationshipCardState'
import {
  ClassicIntroductionError,
  ClassicIntroductionResponse,
  IntroductionRequestResponse,
  createClassicIntroductionRequest,
  createIntroductionRequest
} from '~/api/introductions'

type ClassicIntroductionRequest = IntroductionsApi.ClassicIntroductionRequest

const makeIntroRequest = (formdata: IntroductionRequestSubmission) => {
  const { accountId, id, relationshipId, personId, form, requesterMembership, connectorMembership, prospect } = formdata

  //
  // Make a classic intro request
  if (!!accountId) {
    const data = {
      accountId,
      relationshipLeadId: id,
      requesterId: personId,
      relationshipId,
      ...form
    } as ClassicIntroductionRequest

    return createClassicIntroductionRequest(data)
  }

  //
  // Make a new style intro request
  return createIntroductionRequest({
    requesterId: personId,
    requesterMembershipId: requesterMembership.id,
    connectorMembershipId: connectorMembership.id,
    companyId: prospect.company?.id,
    relationshipId: relationshipId,
    prospectEmail: form.prospectEmail,
    details: form.msgForConnector // <-- What happens to the msgForProspect?
  })
}

// Even with a distributive conditional type, TypeScript can't infer the return type of makeIntroRequest
const relationshipCardStateMachine = buildRelationshipCardStateMachine<
  ClassicIntroductionResponse | ClassicIntroductionError,
  IntroductionRequestResponse
>(makeIntroRequest)

type RelationshipCardProps = {
  relationshipLead: RelationshipLead
  requesterMembership?: Membership
  connectorMembership?: Membership
  disableDisplaySave?: boolean
  onRequestIntro?: () => void
  prospectCompanyCount?: number
  isUnassigned?: boolean
}

export const RelationshipCard = ({
  relationshipLead,
  requesterMembership,
  connectorMembership,
  disableDisplaySave = false
}: RelationshipCardProps) => {
  const { id, requester, connector, prospect, relationship } = relationshipLead
  const { accountId, personId } = useAuth()

  const [currentState, setCurrentState] = useState<RelationshipLeadStates>(relationshipLead.state)
  const [initialState, setInitialState] = useState<RelationshipLeadStates>(relationshipLead.state)

  const stateChangeRequest = RelationshipLeadsApi.useStateChangeRequest()

  const leadsStore = RelationshipLeadStore.useStore((store) => store)

  const toast = useToast()

  const [state, send] = useMachine(relationshipCardStateMachine)

  const showError = (verb: string) => {
    toast({
      description: `An error occurred while ${verb} the relationship lead. Please try again.`,
      status: 'error',
      duration: 5000,
      isClosable: true
    })
  }

  const handleStateChange = (
    state: RelationshipLeadStates,
    dismissedReason: RelationshipLeadDismissedReasons,
    dismissedAt: Date
  ) => {
    const stateChange: RelationshipLeadStateChange = {
      id,
      state,
      dismissedReason,
      dismissedAt: dismissedAt?.toISOString()
    }

    if (currentState !== stateChange.state) {
      setInitialState(currentState)
      setCurrentState(stateChange.state)
    }

    stateChangeRequest.mutate(stateChange, {
      onSuccess: () => leadsStore.changeState(stateChange),
      onError: () => showError('updating')
    })
  }

  let cardContent = null
  let activeBorderColor = 'gray.200'
  let activeBgColor = 'white'

  switch (state.value) {
    case 'displayLinks':
      cardContent = <LinksContent relationshipLead={relationshipLead} onBack={() => send('BACK')} />
      break

    case 'displayHide':
      cardContent = (
        <HideRelationshipContent
          firstName={relationshipLead.prospect.firstName}
          lastName={relationshipLead.prospect.lastName}
          onBack={() => {
            handleStateChange(initialState, null, null)
            send('BACK')
          }}
          onSelect={(reason) => handleStateChange('user_dismissed', reason, new Date())}
        />
      )
      activeBgColor = 'gray.100'
      break

    case 'introRequested':
      cardContent = <RequestIntroductionContent introUrl={state.context.introUrl} />
      activeBorderColor = 'blue.400'
      break

    case 'displaySave':
      if (!disableDisplaySave) {
        cardContent = (
          <SaveRelationshipContent
            firstName={relationshipLead.prospect.firstName}
            lastName={relationshipLead.prospect.lastName}
            savedLeadsUrl={`/saved-leads/${relationshipLead.prospect.company.slug}`}
            onBack={() => {
              handleStateChange(initialState, null, null)
              send('BACK')
            }}
          />
        )
        activeBorderColor = 'yellow.400'
        break
      }
    /* falls through */
    default:
      cardContent = (
        <RelationshipContent onMore={() => send('LINKS')} relationshipLead={relationshipLead}>
          <RelationshipCardButtons
            onHide={() => {
              setInitialState(currentState)
              handleStateChange('user_dismissed', null, new Date())
              send('HIDE')
            }}
            onSave={() => {
              handleStateChange('saved', null, null)
              send('SAVE')
            }}
            onUnsave={() => {
              handleStateChange('queued', null, null)
            }}
            onRequest={() => send('REQUEST')}
            saved={currentState === 'saved'}
          />
        </RelationshipContent>
      )
  }

  const handleCloseModal = () => {
    send('CANCEL')
  }

  const handleOnRequestClick = (formdata: RequestModalBodyForm) => {
    const data = {
      id,
      accountId,
      personId,
      relationshipId: relationship.id,
      connector,
      requesterMembership,
      connectorMembership,
      prospect,
      form: formdata
    } as IntroductionRequestSubmission

    send({ type: 'MAKE_REQUEST', data })
    handleStateChange('intro_requested', null, null)
  }

  return (
    <>
      <Flex
        direction="column"
        width={233}
        height={216}
        border="1px"
        mb={2}
        borderColor={activeBorderColor}
        borderRadius={3}
        bg={activeBgColor}
        boxShadow="0 18px 67px rgba(0,0,0,0.1)"
        _hover={{
          boxShadow: '0 18px 67px rgba(0,0,0,.17)'
        }}
      >
        {cardContent}
      </Flex>
      {state.context.requestIntroModalIsOpen && (
        <RelationshipRequestModal
          requester={requester}
          connector={connector}
          prospect={prospect}
          isOpen={state.context.requestIntroModalIsOpen}
          isLoading={state.context.requestInFlight}
          onClose={handleCloseModal}
          onCancelClick={handleCloseModal}
          onRequestClick={handleOnRequestClick}
        />
      )}
    </>
  )
}
